import { useEffect, useMemo, useState } from "react";
import CommentsForm from "./Components/CommentsForm";

import { Version } from "@/services/global-services/models";
import { PageType } from "@/services/shared-models";
import { CommentEntry, CommentEntryResponse } from "@/services/user-services/models";
import { useDataStore, useFilterStore } from "@/stores";
import { PlusIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { parseJSON } from "date-fns";
import { orderBy } from "lodash";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import Comment from "./Components/Comment";

interface UserCommentProps {
    comments?: Array<CommentEntryResponse>;
    pageType: PageType;
}

const UserComments = ({ comments, pageType }: UserCommentProps) => {
    const versions = useDataStore((state) => state.versions);
    const pageComments = useDataStore((state) => state.comments);
    const activeVersionId = useFilterStore((state) => state.activeVersion);
    const activeVersion = useMemo<Version | undefined>(
        () => versions.find((x) => x.id === activeVersionId),
        [versions, activeVersionId]
    );

    const normalizeComment = (entry: CommentEntryResponse | CommentEntry): CommentEntry => ({
        ...entry,
        commentDate: parseJSON(entry.commentDate as unknown as string),
        replies: entry.replies?.map(normalizeComment) || [],
    });

    const [formVisible, setFormVisible] = useState<boolean>(false);
    const [data, setData] = useState<Array<CommentEntry>>(comments?.map(normalizeComment) || []);

    const { "*": pageSlug } = useParams();

    const { t } = useTranslation("common");
    useEffect(() => {
        if (pageComments[pageSlug!]) {
            setData(pageComments[pageSlug!].map(normalizeComment) || []);

            return;
        }

        if (comments) setData(comments.map(normalizeComment));
        else setData([]);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageComments, comments]);

    const onCommentSubmitted = (comment: CommentEntry) => {
        setFormVisible(false);
        setData([...data, comment]);
    };

    const onCommentCancelled = () => setFormVisible(false);

    const canPostComments = (): boolean => {
        if (pageType === PageType.Content) return true;

        return activeVersion?.isDefault ?? true;
    };

    return (
        <>
            <div id="block-comments" className="flex items-center justify-between">
                <h2 className="text-primary-500">{t("comments.title")}</h2>

                {canPostComments() && (
                    <div className="ml-auto flex items-center gap-2">
                        <button
                            className={clsx([
                                "flex text-white font-medium text-base/normal items-center px-5 py-3 bg-primary-500 hover:bg-primary-600 rounded-lg mr-auto disabled:opacity-50",
                                formVisible ? "cursor-auto pointer-events-none" : "cursor-pointer",
                            ])}
                            onClick={() => setFormVisible(true)}
                            disabled={formVisible}
                        >
                            <PlusIcon className="w-5 h-5 mr-2" />
                            {t("comments.buttons.add")}
                        </button>
                    </div>
                )}
            </div>

            <div className="mt-2">
                <CommentsForm
                    open={formVisible}
                    onSubmit={onCommentSubmitted}
                    onCancel={onCommentCancelled}
                    pageType={pageType}
                    pageSlug={pageSlug}
                />
            </div>

            {data.length === 0 && (
                <div className="flex items-center mb-2">
                    <p className="text-sm mx-auto my-28">{t("comments.empty")}</p>
                </div>
            )}

            {orderBy(data, ["commentDate"], ["desc"]).map((comment) => (
                <Comment key={comment.id} comment={comment} topLevel={true} pageType={pageType} pageSlug={pageSlug} />
            ))}
        </>
    );
};

export default UserComments;
